import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { naira, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Wallet, Send, Clock, CheckCircle2, XCircle, Landmark, AlertCircle, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function WalletCard() {
  const [data, setData] = useState(null);
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [loading, setLoading] = useState(false);

  const load = () => {
    api.get("/filmmaker/wallet").then((r) => setData(r.data)).catch(() => {});
    api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const hasBank = !!(me?.account_number && me?.paystack_recipient_code);
  const canRequest = data.wallet_balance_naira >= data.min_payout_naira && hasBank;
  const hasPending = data.payouts.some((p) => p.status === "pending" || p.status === "otp_required");

  const submit = async () => {
    setLoading(true);
    try {
      await api.post("/filmmaker/payouts/request", {
        amount_naira: Number(amount),
      });
      toast.success("Payout requested. Admin will process shortly.");
      setOpen(false);
      setAmount("");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Request failed");
    } finally {
      setLoading(false);
    }
  };

  const statusBadge = (s) => {
    const m = {
      pending: { cls: "bg-gold/15 text-gold border-gold/30", icon: Clock, label: "Pending" },
      otp_required: { cls: "bg-amber-500/15 text-amber-300 border-amber-500/30", icon: KeyRound, label: "OTP needed" },
      paid: { cls: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30", icon: CheckCircle2, label: "Paid" },
      rejected: { cls: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle, label: "Rejected" },
      failed: { cls: "bg-red-500/15 text-red-400 border-red-500/30", icon: XCircle, label: "Failed" },
    }[s] || { cls: "bg-white/5 border-white/10", icon: Clock, label: s };
    const Icon = m.icon;
    return <span className={`text-xs px-2 py-0.5 rounded-full border ${m.cls} inline-flex items-center gap-1`}><Icon className="w-3 h-3" /> {m.label}</span>;
  };

  return (
    <>
      <div className="rounded-xl bg-surface border border-white/10 p-6" data-testid="wallet-card">
        <div className="flex items-start justify-between gap-3 mb-5">
          <div>
            <p className="text-xs uppercase tracking-widest text-gold font-semibold mb-1 flex items-center gap-1.5">
              <Wallet className="w-3 h-3" /> Wallet
            </p>
            <p className="font-display text-3xl font-black text-gold">{naira(data.wallet_balance_naira)}</p>
            <p className="text-xs text-foreground/50 mt-1">
              Available · platform takes {data.platform_fee_pct}% per rental · min payout {naira(data.min_payout_naira)}
            </p>
          </div>
          <Button
            disabled={!canRequest || hasPending}
            onClick={() => { setAmount(String(data.wallet_balance_naira)); setOpen(true); }}
            className="bg-terracotta hover:bg-ember text-white"
            data-testid="request-payout-btn"
          >
            <Send className="w-4 h-4" /> {hasPending ? "Pending" : "Request payout"}
          </Button>
        </div>

        {!hasBank && (
          <div className="mb-4 rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 flex items-start gap-2.5" data-testid="bank-missing-banner">
            <AlertCircle className="w-4 h-4 text-amber-300 mt-0.5 shrink-0" />
            <p className="text-xs text-foreground/85 leading-relaxed">
              Add your payout bank account in your{" "}
              <Link to="/filmmaker/profile" className="text-gold underline underline-offset-2 hover:text-terracotta" data-testid="bank-add-link">profile</Link>{" "}
              before you can request a payout. We use it to credit your account automatically.
            </p>
          </div>
        )}
        {hasBank && (
          <div className="mb-4 rounded-lg bg-ink/60 border border-white/5 p-3 flex items-center gap-2.5" data-testid="bank-saved-summary">
            <Landmark className="w-4 h-4 text-emerald-400 shrink-0" />
            <p className="text-xs text-foreground/75 leading-snug">
              Payouts credited to <strong className="text-foreground/95">{me.bank_name}</strong> ••••{me.account_number.slice(-4)} ·{" "}
              <Link to="/filmmaker/profile" className="text-terracotta hover:text-ember underline-offset-2 hover:underline">change</Link>
            </p>
          </div>
        )}

        <div className="grid grid-cols-3 gap-3 mb-4">
          <div className="p-3 rounded-lg bg-ink/60 border border-white/5 text-center">
            <p className="text-xs text-foreground/50">Lifetime earned</p>
            <p className="font-display font-bold text-base">{naira(data.lifetime_earnings_naira)}</p>
          </div>
          <div className="p-3 rounded-lg bg-ink/60 border border-white/5 text-center">
            <p className="text-xs text-foreground/50">Pending payout</p>
            <p className="font-display font-bold text-base">{naira(data.pending_payout_naira)}</p>
          </div>
          <div className="p-3 rounded-lg bg-ink/60 border border-white/5 text-center">
            <p className="text-xs text-foreground/50">Recent payouts</p>
            <p className="font-display font-bold text-base">{data.payouts.length}</p>
          </div>
        </div>

        {data.payouts.length > 0 && (
          <div className="space-y-2 mt-3">
            <p className="text-xs uppercase tracking-widest text-foreground/50">Recent</p>
            {data.payouts.slice(0, 4).map((p) => (
              <div key={p.id} className="flex items-center justify-between p-3 rounded-lg bg-ink/60 border border-white/5 text-sm">
                <div>
                  <p className="font-display font-bold">{naira(p.amount_naira)}</p>
                  <p className="text-xs text-foreground/50">{new Date(p.created_at).toLocaleDateString()}</p>
                </div>
                {statusBadge(p.status)}
              </div>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-surface border-white/10 text-foreground">
          <DialogHeader>
            <DialogTitle>Request payout</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Amount</Label>
              <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} max={data.wallet_balance_naira} min={data.min_payout_naira} className="mt-1.5 bg-ink border-white/10 h-11" data-testid="payout-amount-input" />
              <p className="text-xs text-foreground/50 mt-1">Available: {naira(data.wallet_balance_naira)} · Min: {naira(data.min_payout_naira)}</p>
            </div>
            {hasBank && (
              <div className="rounded-lg bg-ink/60 border border-white/5 p-3 text-sm" data-testid="payout-bank-preview">
                <p className="text-xs uppercase tracking-widest text-foreground/50 mb-1 flex items-center gap-1.5">
                  <Landmark className="w-3 h-3" /> Crediting to
                </p>
                <p className="font-display font-bold">{me.account_name}</p>
                <p className="text-foreground/70 text-xs">{me.bank_name} · ••••{me.account_number.slice(-4)}</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={submit} disabled={loading} className="bg-terracotta hover:bg-ember text-white" data-testid="payout-confirm-btn">
              {loading ? "Submitting…" : "Submit request"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
